import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import { X, User, FileText, CheckCircle, Save, AlertCircle, MessageSquare } from "lucide-react";

const API_URL = "https://ims.holdthechild.net/api";

const AddDisciplinaryCaseForm = ({ onClose, onSuccess, caseToEdit }) => {
  const [formData, setFormData] = useState({
    staffId: "",
    department: "",
    offense: "",
    actionTaken: "",
    status: "pending",
    resolutionNotes: "",
  });

  const [staffList, setStaffList] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const modalRef = useRef(null);

  const staffOptions = staffList.map((staff) => ({
    value: staff._id,
    label: `${staff.firstName} ${staff.lastName} — ${staff.jobTitle || "No Title"}`,
    department: staff.department || "",
  }));

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/staff/current-user`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCurrentUserId(res.data.data._id);
      } catch (err) {
        console.error("Failed to fetch current user ID", err);
        setError("Failed to get current user information");
      }
    };

    if (!caseToEdit) {
      fetchCurrentUser();
    }
  }, [token, caseToEdit]);

  useEffect(() => {
    if (caseToEdit) {
      setFormData({
        staffId: caseToEdit.staffId?._id || caseToEdit.staffId || "",
        department: caseToEdit.department || "",
        offense: caseToEdit.offense || "",
        actionTaken: caseToEdit.actionTaken || "",
        status: caseToEdit.status || "pending",
        resolutionNotes: caseToEdit.resolutionNotes || "",
      });
    }
  }, [caseToEdit]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/staff`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStaffList(res.data.data || []);
      } catch (err) {
        toast.error("Failed to load staff list");
        setError("Failed to load staff list");
      }
    };
    fetchStaff();
  }, [token]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const caseId = caseToEdit?._id || caseToEdit?.id;
    const endpoint = caseToEdit
      ? `${API_URL}/disciplinary-cases/${caseId}`
      : `${API_URL}/disciplinary-cases`;

    const method = caseToEdit ? "put" : "post";

    try {
      const payload = {
        ...formData,
      };

      if (!caseToEdit) {
        if (!currentUserId) {
          setError("Could not get current user information. Please try again.");
          setLoading(false);
          return;
        }
        // When creating, we send resolutionNotes as the briefExplanation (mapped in controller)
        // But here for the boss creating directly, we just send standard payload
        payload.reportedBy = currentUserId; 
      }

      await axios[method](endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Case ${caseToEdit ? "updated" : "added"} successfully`);
      onSuccess?.();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to submit case";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div
          ref={modalRef}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
        >
          {/* Header */}
          <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
            <div className="flex items-center justify-between px-8 py-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-black">
                    {caseToEdit ? "Take Disciplinary Action" : "Add New Disciplinary Case"}
                  </h2>
                  <p className="text-lg text-black-100 mt-1">
                    {caseToEdit ? "Review & Resolve" : "Employee Management"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-grow px-8 py-6 overflow-y-auto">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium">Error</p>
                    <p className="mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Show Reporter's Description if we are editing (taking action) */}
              {caseToEdit && (
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-blue-800 mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>Reporter's Brief Explanation</span>
                  </label>
                  <p className="text-sm text-gray-700 italic bg-white p-3 rounded border border-blue-100">
                    {caseToEdit.briefExplanation || "No description provided by reporter."}
                  </p>
                  {caseToEdit.createdAt && (
                    <p className="text-xs text-blue-500 mt-2 text-right">
                      Reported on: {new Date(caseToEdit.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Staff</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <Select
                    options={staffOptions}
                    value={staffOptions.find(option => option.value === formData.staffId)}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        staffId: selected?.value || "",
                        department: selected?.department || "",
                      }))
                    }
                    placeholder="Select Staff..."
                    isClearable
                    isDisabled={!!caseToEdit} // Disable staff change when editing
                    className="rounded-xl"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.75rem',
                        minHeight: '44px',
                        paddingLeft: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': { borderColor: '#F97316' },
                        backgroundColor: caseToEdit ? '#F3F4F6' : 'white',
                      }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' }),
                      singleValue: (base) => ({ ...base, color: '#1F2937' }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                      }),
                    }}
                    getOptionLabel={(e) => (
                      <div className="flex flex-col">
                        <span className="font-medium">{e.label.split(" — ")[0]}</span>
                        <span className="text-sm text-gray-500">{e.label.split(" — ")[1]}</span>
                      </div>
                    )}
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Department</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 text-gray-600 focus:outline-none"
                    placeholder="Department"
                    readOnly
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Offense</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    name="offense"
                    value={formData.offense}
                    onChange={handleChange}
                    rows={3}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                    placeholder="Describe the offense..."
                    required
                    readOnly={!!caseToEdit} // Usually offense shouldn't change when taking action
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Action Taken / Resolution Notes</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                    <CheckCircle className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    name="resolutionNotes" // Mapped to resolutionNotes in DB
                    value={formData.resolutionNotes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                    placeholder={caseToEdit ? "Describe the action taken to resolve this case..." : "Initial notes..."}
                    required={!!caseToEdit} // Required when taking action
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Status</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CheckCircle className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none bg-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>All fields marked as required must be completed</span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  form="kpi-form"
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${loading
                      ? 'bg-orange-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                    }`}
                >
                  {loading ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>
                        {caseToEdit ? "Updating..." : "Submitting..."}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="w-5 h-5" />
                      <span>{caseToEdit ? "Update Case" : "Submit Case"}</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddDisciplinaryCaseForm;